import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

/**
 * Servicio para gestión de Alumnos
 * Maneja comunicación con API del backend
 */
class StudentsService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_URL}/students`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para agregar token de autenticación
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('adminToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Interceptor para manejar errores
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expirado o inválido
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminData');
          window.location.href = '/admin';
        }
        return Promise.reject(error);
      }
    );
  }

  // Obtener todos los Alumnos
  async getStudents(params = {}) {
    try {
      const response = await this.api.get('/', { params });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener Alumnos');
      throw new Error(errorMessage);
    }
  }

  // Obtener Alumnos activos
  async getActiveStudents() {
    try {
      const response = await this.api.get('/active');
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener Alumnos activos');
      throw new Error(errorMessage);
    }
  }

  // Buscar Alumnos por nombre
  async searchStudents(searchTerm) {
    try {
      const response = await this.api.get('/', {
        params: { search: searchTerm }
      });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al buscar Alumnos');
      throw new Error(errorMessage);
    }
  }

  // Crear Alumno
  async createStudent(studentData) {
    try {
      const response = await this.api.post('/', studentData);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al crear Alumno');
      throw new Error(errorMessage);
    }
  }

  // Actualizar Alumno
  async updateStudent(id, studentData) {
    try {
      const response = await this.api.patch(`/${id}`, studentData);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al actualizar Alumno');
      throw new Error(errorMessage);
    }
  }

  // Eliminar Alumno
  async deleteStudent(id) {
    try {
      await this.api.delete(`/${id}`);
      return true;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al eliminar Alumno');
      throw new Error(errorMessage);
    }
  }

  // Importar Alumnos desde Excel
  async importStudentsFromExcel(formData) {
    try {
      const response = await this.api.post('/import-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al importar Alumnos desde Excel');
      throw new Error(errorMessage);
    }
  }

  // Obtener Alumnos por evento (para filtrar por access codes)
  async getStudentsByEvent(eventId) {
    try {
      const response = await this.api.get('/by-event', {
        params: { eventId }
      });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener Alumnos por evento');
      throw new Error(errorMessage);
    }
  }

  // Exportar Alumnos a Excel
  async exportStudents(filters = {}) {
    try {
      // Send filters in request body instead of query parameters
      const requestData = {
        search: filters.search || '',
        eventId: filters.eventId || 'all',
        status: filters.status || 'all',
        accessCode: filters.accessCode || '',
      };
      
      const response = await this.api.post('/export', requestData, {
        responseType: 'blob'
      });
      
      // Crear un blob y descargar el archivo
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Alumnos.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al exportar Alumnos');
      throw new Error(errorMessage);
    }
  }

  // Obtener template de Excel para importación
  async downloadTemplate() {
    try {
      const response = await this.api.get('/template', {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template_Alumnos.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al descargar template');
      throw new Error(errorMessage);
    }
  }

  // Manejar errores de API
  getErrorMessage(error, defaultMessage) {
    if (error.response) {
      // Error del servidor
      const { status, data } = error.response;
      
      if (status === 401) {
        return 'No tienes autorización para realizar esta acción';
      }
      
      if (status === 403) {
        return 'No tienes permisos para acceder a esta información';
      }
      
      if (status === 404) {
        return 'Alumno no encontrado';
      }
      
      if (status === 409) {
        return data.message;
      }
      
      if (status === 422 && data?.message) {
        return data.message;
      }
      
      if (data?.message) {
        return data.message;
      }
    }
    
    if (error.request) {
      return 'No se pudo conectar con el servidor. Verifica tu conexión a internet.';
    }
    
    return defaultMessage;
  }
}

// Exportar instancia singleton
export const studentsService = new StudentsService();
export default studentsService;